import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ReportShell } from "@/features/reports/ReportShell";
import { useCondemnationRegister } from "@/features/condemnation/api";
import { CONDEMNATION_REASONS, CONDEMNATION_STATUSES } from "@/features/condemnation/types";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/condemnation-register")({
  component: CondemnationRegisterReport,
});

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function CondemnationRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [reason, setReason] = useState<string>("all");
  const { currentBranchId } = useBranch();
  const { data: rows = [], isLoading } = useCondemnationRegister(currentBranchId);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filters.departmentId && r.department_id !== filters.departmentId) return false;
      if (filters.categoryId && r.category_id !== filters.categoryId) return false;
      if (filters.status && r.status !== filters.status) return false;
      if (reason !== "all" && r.reason !== reason) return false;
      if (filters.dateFrom && (r.created_at ?? "") < filters.dateFrom) return false;
      if (filters.dateTo && (r.created_at ?? "") > filters.dateTo + "T23:59:59") return false;
      return true;
    });
  }, [rows, filters, reason]);

  const exportRows = useMemo(() => () => filtered.map((r) => ({
    RequestNo: r.request_no,
    AssetCode: r.asset_code ?? "",
    AssetName: r.asset_name ?? "",
    Department: r.department_name ?? "",
    Location: r.location_name ?? "",
    Category: r.category_name ?? "",
    Reason: r.reason ?? "",
    RepairEstimate: r.repair_cost_estimate ?? 0,
    ReplacementEstimate: r.replacement_cost_estimate ?? 0,
    TechnicalStatus: r.technical_status ?? "Pending",
    FinanceStatus: r.finance_status ?? "Pending",
    CommitteeStatus: r.committee_status ?? "Pending",
    FinalDecisionDate: r.final_decision_at ?? "",
    AssetStatus: r.asset_status ?? "",
    Remarks: r.reason_detail ?? "",
  })), [filtered]);

  return (
    <ReportShell id="R-14" title="Condemnation Register"
      description="All condemnation requests with technical, finance and committee review status."
      filters={filters} onChange={setFilters}
      enabled={["departmentId", "categoryId", "status", "dateFrom", "dateTo"]}
      exportRows={exportRows}
      rightActions={
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Reason" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reasons</SelectItem>
            {CONDEMNATION_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      }>
      <div className="text-xs text-muted-foreground">
        Status filter accepts: {CONDEMNATION_STATUSES.join(", ")}
      </div>
      <Card><CardContent className="p-0 overflow-x-auto">
        {isLoading ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Request No</TableHead>
              <TableHead>Asset Code</TableHead>
              <TableHead>Asset Name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Repair Est.</TableHead>
              <TableHead className="text-right">Replacement Est.</TableHead>
              <TableHead>Technical</TableHead>
              <TableHead>Finance</TableHead>
              <TableHead>Committee</TableHead>
              <TableHead>Final Date</TableHead>
              <TableHead>Asset Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.slice(0, 500).map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">
                    <Link to="/condemnation/$id" params={{ id: r.id }} className="text-primary hover:underline">{r.request_no}</Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.asset_id ? (
                      <Link to="/assets/$assetId" params={{ assetId: r.asset_id }} className="text-primary hover:underline">{r.asset_code}</Link>
                    ) : r.asset_code}
                  </TableCell>
                  <TableCell>{r.asset_name ?? "—"}</TableCell>
                  <TableCell>{r.department_name ?? "—"}</TableCell>
                  <TableCell>{r.location_name ?? "—"}</TableCell>
                  <TableCell>{r.category_name ?? "—"}</TableCell>
                  <TableCell className="text-xs">{r.reason ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.repair_cost_estimate ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.replacement_cost_estimate ?? "—"}</TableCell>
                  <TableCell><StageBadge value={r.technical_status} /></TableCell>
                  <TableCell><StageBadge value={r.finance_status} /></TableCell>
                  <TableCell><StageBadge value={r.committee_status ?? r.status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.final_decision_at)}</TableCell>
                  <TableCell><Badge variant="outline">{r.asset_status ?? "—"}</Badge></TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={14} className="py-8 text-center text-sm text-muted-foreground">No condemnation requests match the current filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
        <div className="border-t p-3 text-xs text-muted-foreground">
          {filtered.length > 500 ? `Showing first 500 of ${filtered.length}. Export CSV for full list.` : `Showing ${filtered.length} of ${filtered.length}.`}
        </div>
      </CardContent></Card>
    </ReportShell>
  );
}

function StageBadge({ value }: { value: string | null | undefined }) {
  const v = value ?? "Pending";
  const tone =
    v === "Approved" ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" :
    v === "Rejected" ? "bg-red-500/10 text-red-700 border-red-500/30" :
    v === "Under Review" ? "bg-amber-500/10 text-amber-700 border-amber-500/30" :
    "bg-muted text-muted-foreground border-border";
  return <Badge variant="outline" className={tone}>{v}</Badge>;
}
